
import { FSharpException, Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { bigint_type, record_type, option_type, class_type, array_type, list_type, tuple_type, string_type, int32_type, uint32_type, union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { Ram_$reflection } from "./RamStore.js";
import { WidthInferError_$reflection, Port_$reflection, PortType_$reflection, ComponentType_$reflection } from "../Common/CommonTypes.js";
import { values, fold, filter, map as map_1, iterate, toList, ofList, isEmpty, FSharpMap__get_Count, add, containsKey, find, tryFind, empty } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { compare, Exception, equals, comparePrimitives } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { collect, empty as empty_1, cons, reverse, mapIndexed, forAll, length, head, tail, singleton, append, ofArray, fold as fold_1, isEmpty as isEmpty_1, map } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { interpolate, join, toFail, printf, toText } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { item, map as map_2 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { op_RightShift, op_BitwiseAnd, op_UnaryNegation, compare as compare_1, op_Modulus, fromUInt32, op_Addition, equals as equals_1, toString, fromZero, toUInt32, fromOne, op_LeftShift, op_Subtraction } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { map as map_3, delay, toList as toList_1, toArray } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { warn } from "../Shared/Log.js";

/**
 * Binary data used in simulation
 */
export class Bit extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Zero", "One"];
    }
    static Zero = new Bit(0, []);
    static One = new Bit(1, []);
}

export function Bit_$reflection() {
    return union_type("SimGraphTypes.Bit", [], Bit, () => [[], []]);
}

/**
 * State (possibly none) remembered by component
 * from previous clock cycle. Combinational components
 * have no state.
 */
export class SimulationComponentState extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["NoState", "DffState", "RegisterState", "RamState"];
    }
    static NoState = new SimulationComponentState(0, []);
}

export function SimulationComponentState_$reflection() {
    return union_type("SimGraphTypes.SimulationComponentState", [], SimulationComponentState, () => [[], [["Item", uint32_type]], [["Item", FastData_$reflection()]], [["Item", Ram_$reflection()]]]);
}

/**
 * Like Component but with additional dynamic info used by simulator
 * Clocked components have state data.
 * All components have optional data on inputs that propagates
 * During evaluation of combinational logic
 * Components require all inputs to have data before they can
 * generate output data
 * Note that reducer is a function that generates the outputs
 * TODO: make this equatable data?
 */
export class SimulationComponent extends Record {
    constructor(Id, Type, Label, Outputs, DrivenInputWidths, InputWidths, OutputWidths, CustomSimulationGraph) {
        super();
        this.Id = Id;
        this.Type = Type;
        this.Label = Label;
        this.Outputs = Outputs;
        this.DrivenInputWidths = DrivenInputWidths;
        this.InputWidths = InputWidths;
        this.OutputWidths = OutputWidths;
        this.CustomSimulationGraph = CustomSimulationGraph;
    }
}

export function SimulationComponent_$reflection() {
    return record_type("SimGraphTypes.SimulationComponent", [], SimulationComponent, () => [["Id", int32_type], ["Type", ComponentType_$reflection()], ["Label", string_type], ["Outputs", array_type(list_type(tuple_type(int32_type, int32_type)))], ["DrivenInputWidths", list_type(tuple_type(tuple_type(int32_type, int32_type), int32_type))], ["InputWidths", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, int32_type])], ["OutputWidths", array_type(int32_type)], ["CustomSimulationGraph", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, SimulationComponent_$reflection()]))]]);
}

export class SimulationErrorType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["PortNumMissing", "WrongPortType", "ConnTypeHasNum", "LabelConnect", "LabelDuplicate", "WidthMismatch", "InferConnWidths", "BadName", "MissingSheet", "InPortMismatch", "OutPortMismatch", "InputConnError", "OutputConnError", "LabelConnError", "CycleDetected", "AlgInpNotAllowed", "DependencyNotFound", "WrongSelection", "UnnecessaryNC", "InternalError", "GenericSimError"];
    }
    static LabelConnect = new SimulationErrorType(3, []);
    static UnnecessaryNC = new SimulationErrorType(18, []);
}

export function SimulationErrorType_$reflection() {
    return union_type("SimGraphTypes.SimulationErrorType", [], SimulationErrorType, () => [[["Item", PortType_$reflection()]], [["Item1", PortType_$reflection()], ["Item2", Port_$reflection()]], [["Item1", PortType_$reflection()], ["Item2", int32_type]], [], [["Item1", string_type], ["Item2", string_type]], [["Item", WidthInferError_$reflection()]], [["Item", string_type]], [["Item", string_type]], [["Item", string_type]], [["Item1", string_type], ["Item2", string_type], ["Item3", string_type]], [["Item1", string_type], ["Item2", string_type], ["Item3", string_type]], [["Item1", int32_type], ["Item2", Port_$reflection()], ["Item3", PortRmInfo_$reflection()]], [["Item1", int32_type], ["Item2", Port_$reflection()], ["Item3", PortRmInfo_$reflection()]], [["Item", int32_type]], [["Item", string_type]], [["Item", string_type]], [["Item", string_type]], [["Item", string_type]], [], [["Item", class_type("System.Exception")]], [["Item", string_type]]]);
}

/**
 * - Documents an error found while simulating.
 * - Should never happen
 */
export class SimulationError extends Record {
    constructor(ErrType, InDependency, ComponentsAffected, ConnectionsAffected) {
        super();
        this.ErrType = ErrType;
        this.InDependency = InDependency;
        this.ComponentsAffected = ComponentsAffected;
        this.ConnectionsAffected = ConnectionsAffected;
    }
}

export function SimulationError_$reflection() {
    return record_type("SimGraphTypes.SimulationError", [], SimulationError, () => [["ErrType", SimulationErrorType_$reflection()], ["InDependency", option_type(string_type)], ["ComponentsAffected", list_type(int32_type)], ["ConnectionsAffected", list_type(int32_type)]]);
}

export class PortRmInfo extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Unremovable", "Removable"];
    }
    static Unremovable = new PortRmInfo(0, []);
}

export function PortRmInfo_$reflection() {
    return union_type("SimGraphTypes.PortRmInfo", [], PortRmInfo, () => [[], [["Item", ComponentType_$reflection()]]]);
}

export class FastBits extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Word", "BigWord"];
    }
}

export function FastBits_$reflection() {
    return union_type("SimGraphTypes.FastBits", [], FastBits, () => [[["dat", uint32_type]], [["dat", bigint_type]]]);
}

export class FastData extends Record {
    constructor(Dat, Width) {
        super();
        this.Dat = Dat;
        this.Width = (Width | 0);
    }
}

export function FastData_$reflection() {
    return record_type("SimGraphTypes.FastData", [], FastData, () => [["Dat", FastBits_$reflection()], ["Width", int32_type]]);
}

export class BinaryOp extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["AddOp", "SubOp", "BitAndOp", "BitOrOp", "BitXorOp"];
    }
    static AddOp = new BinaryOp(0, []);
    static SubOp = new BinaryOp(1, []);
    static BitAndOp = new BinaryOp(2, []);
    static BitOrOp = new BinaryOp(3, []);
    static BitXorOp = new BinaryOp(4, []);
}

export function BinaryOp_$reflection() {
    return union_type("SimGraphTypes.BinaryOp", [], BinaryOp, () => [[], [], [], [], []]);
}

export class UnaryOp extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["NegOp", "NotOp", "BitRangeOp", "CarryOfOp"];
    }
    static NegOp = new UnaryOp(0, []);
    static NotOp = new UnaryOp(1, []);
    static CarryOfOp = new UnaryOp(3, []);
}

export function UnaryOp_$reflection() {
    return union_type("SimGraphTypes.UnaryOp", [], UnaryOp, () => [[], [], [["Lower", int32_type], ["Upper", int32_type]], []]);
}

export class ComparisonOp extends Union {
    constructor() {
        super();
        this.tag = 0;
        this.fields = [];
    }
    cases() {
        return ["Equals"];
    }
    static Equals = new ComparisonOp();
}

export function ComparisonOp_$reflection() {
    return union_type("SimGraphTypes.ComparisonOp", [], ComparisonOp, () => [[]]);
}

export class FastAlgExp extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["SingleTerm", "DataLiteral", "UnaryExp", "BinaryExp", "ComparisonExp", "AppendExp"];
    }
}

export function FastAlgExp_$reflection() {
    return union_type("SimGraphTypes.FastAlgExp", [], FastAlgExp, () => [[["Item", tuple_type(int32_type, string_type, int32_type)]], [["Item", FastData_$reflection()]], [["Op", UnaryOp_$reflection()], ["Exp", FastAlgExp_$reflection()]], [["Exp1", FastAlgExp_$reflection()], ["Op", BinaryOp_$reflection()], ["Exp2", FastAlgExp_$reflection()]], [["Exp", FastAlgExp_$reflection()], ["Op", ComparisonOp_$reflection()], ["Item3", bigint_type]], [["Item", list_type(FastAlgExp_$reflection())]]]);
}

/**
 * Raised when an Algebraic case is found in FastSim which has not been implemented,
 * or does not make sense to implement.
 */
export class AlgebraNotImplemented extends FSharpException {
    constructor(Data0) {
        super();
        this.Data0 = Data0;
    }
}

export function AlgebraNotImplemented_$reflection() {
    return class_type("SimGraphTypes.AlgebraNotImplemented", undefined, AlgebraNotImplemented, class_type("System.Exception"));
}

export class FSInterface extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["IData", "IAlg"];
    }
}

export function FSInterface_$reflection() {
    return union_type("SimGraphTypes.FSInterface", [], FSInterface, () => [[["Item", FastData_$reflection()]], [["Item", FastAlgExp_$reflection()]]]);
}

export const SimGraph_empty = empty({
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
});

export function SimGraph_tryFind(_arg, g) {
    return tryFind(_arg, g);
}

export function SimGraph_find(_arg, g) {
    return find(_arg, g);
}

export function SimGraph_containsKey(_arg, g) {
    return containsKey(_arg, g);
}

export function SimGraph_add(_arg, comp, g) {
    return add(_arg, comp, g);
}

export function SimGraph_count(g) {
    return FSharpMap__get_Count(g) | 0;
}

export function SimGraph_isEmpty(g) {
    return isEmpty(g);
}

export function SimGraph_ofList(pairs) {
    return ofList(map((tupledArg) => [tupledArg[0], tupledArg[1]], pairs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
}

export function SimGraph_toList(g) {
    return map((tupledArg) => [tupledArg[0], tupledArg[1]], toList(g));
}

export function SimGraph_iter(f, g) {
    iterate((c, comp) => {
        f(c, comp);
    }, g);
}

export function SimGraph_map(f, g) {
    return map_1(f, g);
}

export function SimGraph_filter(f, g) {
    return filter(f, g);
}

export function SimGraph_fold(f, state, g) {
    return fold(f, state, g);
}

export function SimGraph_values(g) {
    return values(g);
}

export function SimGraph_iterValues(f, g) {
    iterate((_arg, comp) => {
        f(comp);
    }, g);
}

export function SimGraph_mapValues(f, g) {
    return map_1((_arg, comp) => f(comp), g);
}

export function SimGraph_filterValues(f, g) {
    return filter((_arg, comp) => f(comp), g);
}

export function SimGraph_foldValues(f, state, g) {
    return fold((s, _arg, comp) => f(s, comp), state, g);
}

function portTypeName(pType) {
    if (pType.tag === 1) {
        return "output";
    }
    else {
        return "input";
    }
}

const damagedSheetAdvice = "\n\nThis is not something that can be caused by editing a schematic: the sheet file is probably damaged. Try the most recent snapshot in the project\'s \'backup\' folder, and please report it (Info -> Bug Reports).";

export function errMsg(errType) {
    switch (errType.tag) {
        case 1: {
            const port = errType.fields[1];
            let which;
            const matchValue = port.PortNumber;
            if (matchValue == null) {
                which = "A port of this component";
            }
            else {
                const n = matchValue | 0;
                which = toText(printf("Port %d of this component"))(n);
            }
            const arg_4 = portTypeName(errType.fields[0]);
            const arg_5 = portTypeName(port.PortType);
            return toText(printf("%s should be an %s port but is recorded as an %s port.%s"))(which)(arg_4)(arg_5)(damagedSheetAdvice);
        }
        case 2: {
            const arg_7 = portTypeName(errType.fields[0]);
            return toText(printf("This component has an %s port carrying a port number (%d) that it should not have.%s"))(arg_7)(errType.fields[1])(damagedSheetAdvice);
        }
        case 3:
            return toText(printf("You can\'t connect two Net Labels with a wire. Delete the connecting wire. If you want to join two net labels you need only give them the same name - then they will form a single net."));
        case 4:
            return toText(printf("Two %s components cannot have the same label: %s."))(errType.fields[0])(errType.fields[1]);
        case 5:
            return errType.fields[0].Msg;
        case 6:
            return errType.fields[0];
        case 7:
            return errType.fields[0];
        case 8:
            return toText(printf("Can\'t find a design sheet named %s for the custom component of this name"))(errType.fields[0]);
        case 9:
            return toText(printf("This component is an instance of sheet \'%s\', but its inputs no longer match that sheet\'s - the sheet has been edited since the instance was placed.\n\nInstance inputs: %s\nSheet inputs: %s\n\nDelete this component and place it again from the Catalogue to bring it up to date."))(errType.fields[0])(errType.fields[1])(errType.fields[2]);
        case 10:
            return toText(printf("This component is an instance of sheet \'%s\', but its outputs no longer match that sheet\'s - the sheet has been edited since the instance was placed.\n\nInstance outputs: %s\nSheet outputs: %s\n\nDelete this component and place it again from the Catalogue to bring it up to date."))(errType.fields[0])(errType.fields[1])(errType.fields[2]);
        case 11: {
            const count = errType.fields[0] | 0;
            if (count === 0) {
                if (errType.fields[2].tag === 0) {
                    return "Every component input port must be connected: but no connection was found Please connect this input port to the output of another component or an input component.";
                }
                else {
                    return "Every component input port must be connected: but no connection was found";
                }
            }
            else {
                return toText(printf("A component input port must have precisely one driving component, but %d were found. If you want to merge wires together use a MergeWires component, not direct connection."))(count);
            }
        }
        case 12: {
            const count_1 = errType.fields[0] | 0;
            if (count_1 === 0) {
                return "A component output port must have at least one connection. If the component output is meant to be disconnected you can add a \"Not Connected\" component to stop this error";
            }
            else {
                return toText(printf("A component output port has %d connections, which is more than this check allows. An output may drive any number of inputs, so if you are seeing this please report it (Info -> Bug Reports)."))(count_1);
            }
        }
        case 13: {
            const count_2 = errType.fields[0] | 0;
            if (count_2 === 0) {
                return "A set of labelled wires must be driven (on the input of one of the labels): but no such driver was found";
            }
            else {
                return toText(printf("A set of labelled wires must have precisely one driving component, but %d were found. If you are driving two labels from the same component delete one of them: a set of labels with the same name are all connected together and only one label in each same-name set must be driven."))(count_2);
            }
        }
        case 14:
            return errType.fields[0];
        case 15:
            return errType.fields[0];
        case 16:
            return toText(printf("Could not resolve dependency: \"%s\". Make sure a dependency with such name exists in the current project."))(errType.fields[0]);
        case 17:
            return errType.fields[0];
        case 18:
            return "Unnecessary \'Not Connected\' components at adder COUTs";
        case 19: {
            const e = errType.fields[0];
            const arg_23 = e.message;
            const arg_24 = e.stack;
            return toText(printf("Issie\'s simulator has hit an internal problem. This is a fault in Issie, not in your design, and we would like to know about it: please report it with the text below and your project (Info -> Bug Reports).\n\n%s\n\n%s"))(arg_23)(arg_24);
        }
        case 20:
            return errType.fields[0];
        default: {
            const arg = portTypeName(errType.fields[0]);
            return toText(printf("This component has an %s port with no port number.%s"))(arg)(damagedSheetAdvice);
        }
    }
}

/**
 * Calculates and returns the expected width of an Algebraic Expression
 */
export function getAlgExpWidth(exp_mut) {
    getAlgExpWidth:
    while (true) {
        const exp = exp_mut;
        switch (exp.tag) {
            case 1:
                return exp.fields[0].Width | 0;
            case 2:
                switch (exp.fields[0].tag) {
                    case 2:
                        return ((exp.fields[0].fields[1] - exp.fields[0].fields[0]) + 1) | 0;
                    case 3:
                        return 1;
                    default: {
                        exp_mut = exp.fields[1];
                        continue getAlgExpWidth;
                    }
                }
            case 3: {
                const w1 = getAlgExpWidth(exp.fields[0]) | 0;
                const w2 = getAlgExpWidth(exp.fields[2]) | 0;
                if (w1 > w2) {
                    return w1 | 0;
                }
                else {
                    return w2 | 0;
                }
            }
            case 4:
                return 1;
            case 5:
                if (isEmpty_1(exp.fields[0])) {
                    return toFail(printf("what? List in AppendExp is empty")) | 0;
                }
                else {
                    return fold_1((w_1, exp_2) => ((w_1 + getAlgExpWidth(exp_2)) | 0), 0, exp.fields[0]) | 0;
                }
            default:
                return exp.fields[0][2] | 0;
        }
        break;
    }
}

export const bigIntMaskA = map_2((width) => op_Subtraction(op_LeftShift(fromOne(), width), fromOne()), toArray(rangeDouble(0, 1, 128)));

export const bigIntBitMaskA = map_2((width) => op_LeftShift(fromOne(), width), toArray(rangeDouble(0, 1, 128)));

/**
 * all bits with numbers < width = 1
 */
export function bigIntMask(width) {
    if (width <= 128) {
        return item(width, bigIntMaskA);
    }
    else {
        return op_Subtraction(op_LeftShift(fromOne(), width), fromOne());
    }
}

/**
 * single bit 1 (2 ** pos)
 */
export function bigIntBitMask(pos) {
    if (pos <= 128) {
        return item(pos, bigIntBitMaskA);
    }
    else {
        return op_LeftShift(fromOne(), pos);
    }
}

/**
 * A DataLiteral holding the given non-negative value, respecting the representation
 * invariant that Word is used iff width <= 32
 */
export function valueLiteral(width, value) {
    if (width <= 32) {
        return new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* Word */ 0, [toUInt32(value) >>> 0]), width)]);
    }
    else {
        return new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* BigWord */ 1, [value]), width)]);
    }
}

export function zeroLiteral(width) {
    return valueLiteral(width, fromZero());
}

export function allOnesLiteral(width) {
    return valueLiteral(width, bigIntMask(width));
}

/**
 * The structural form of 2*exp truncated to width: exp's bits shifted up one, LSB 0.
 * Degenerates to the constant 0 at width 1 (2A = 0 mod 2). Doubling also has the
 * arithmetic form A + A; the two simplify in different contexts - the structural form
 * merges with bit ranges and appends, the arithmetic form cancels against negated terms.
 */
export function doubleExp(width, exp) {
    if (width === 1) {
        return zeroLiteral(1);
    }
    else {
        return new FastAlgExp(/* AppendExp */ 5, [ofArray([new FastAlgExp(/* UnaryExp */ 2, [new UnaryOp(/* BitRangeOp */ 2, [0, width - 2]), exp]), zeroLiteral(1)])]);
    }
}

export function flattenNestedArithmetic(exp_mut) {
    flattenNestedArithmetic:
    while (true) {
        const exp = exp_mut;
        let matchResult, left, right, left_1, right_1, e_2;
        switch (exp.tag) {
            case 3: {
                switch (exp.fields[1].tag) {
                    case 0: {
                        matchResult = 0;
                        left = exp.fields[0];
                        right = exp.fields[2];
                        break;
                    }
                    case 1: {
                        matchResult = 1;
                        left_1 = exp.fields[0];
                        right_1 = exp.fields[2];
                        break;
                    }
                    default:
                        matchResult = 3;
                }
                break;
            }
            case 2: {
                if (exp.fields[0].tag === 1) {
                    matchResult = 2;
                    e_2 = exp.fields[1];
                }
                else {
                    matchResult = 3;
                }
                break;
            }
            default:
                matchResult = 3;
        }
        switch (matchResult) {
            case 0:
                return append(flattenNestedArithmetic(left), flattenNestedArithmetic(right));
            case 1: {
                const rhs = map((exp_1) => {
                    let matchResult_1, e, e_1;
                    if (exp_1.tag === 2) {
                        if (exp_1.fields[0].tag === 0) {
                            matchResult_1 = 0;
                            e = exp_1.fields[1];
                        }
                        else {
                            matchResult_1 = 1;
                            e_1 = exp_1;
                        }
                    }
                    else {
                        matchResult_1 = 1;
                        e_1 = exp_1;
                    }
                    switch (matchResult_1) {
                        case 0:
                            return e;
                        default:
                            return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NegOp, e_1]);
                    }
                }, flattenNestedArithmetic(right_1));
                return append(flattenNestedArithmetic(left_1), rhs);
            }
            case 2: {
                exp_mut = (new FastAlgExp(/* BinaryExp */ 3, [new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NegOp, new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* Word */ 0, [1]), getAlgExpWidth(e_2))])]), BinaryOp.SubOp, e_2]));
                continue flattenNestedArithmetic;
            }
            default:
                return singleton(exp);
        }
        break;
    }
}

export function assembleArithmetic(width, expLst) {
    const assemble = (stateExp, currentExp) => {
        let matchResult, e;
        if (currentExp.tag === 2) {
            if (currentExp.fields[0].tag === 0) {
                matchResult = 0;
                e = currentExp.fields[1];
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return new FastAlgExp(/* BinaryExp */ 3, [stateExp, BinaryOp.SubOp, e]);
            default:
                return new FastAlgExp(/* BinaryExp */ 3, [stateExp, BinaryOp.AddOp, currentExp]);
        }
    };
    let matchResult_1, exp, exp_1, expN, exp1, exp2, exp_2, expN_1, tl, exp1_1, exp2_1, tl_1;
    if (!isEmpty_1(expLst)) {
        if (!isEmpty_1(tail(expLst))) {
            if (isEmpty_1(tail(tail(expLst)))) {
                if (head(expLst).tag === 2) {
                    if (head(expLst).fields[0].tag === 0) {
                        matchResult_1 = 2;
                        exp_1 = head(tail(expLst));
                        expN = head(expLst).fields[1];
                    }
                    else if (head(tail(expLst)).tag === 2) {
                        if (head(tail(expLst)).fields[0].tag === 0) {
                            matchResult_1 = 2;
                            exp_1 = head(expLst);
                            expN = head(tail(expLst)).fields[1];
                        }
                        else {
                            matchResult_1 = 3;
                            exp1 = head(expLst);
                            exp2 = head(tail(expLst));
                        }
                    }
                    else {
                        matchResult_1 = 3;
                        exp1 = head(expLst);
                        exp2 = head(tail(expLst));
                    }
                }
                else if (head(tail(expLst)).tag === 2) {
                    if (head(tail(expLst)).fields[0].tag === 0) {
                        matchResult_1 = 2;
                        exp_1 = head(expLst);
                        expN = head(tail(expLst)).fields[1];
                    }
                    else {
                        matchResult_1 = 3;
                        exp1 = head(expLst);
                        exp2 = head(tail(expLst));
                    }
                }
                else {
                    matchResult_1 = 3;
                    exp1 = head(expLst);
                    exp2 = head(tail(expLst));
                }
            }
            else if (head(expLst).tag === 2) {
                if (head(expLst).fields[0].tag === 0) {
                    matchResult_1 = 4;
                    exp_2 = head(tail(expLst));
                    expN_1 = head(expLst).fields[1];
                    tl = tail(tail(expLst));
                }
                else if (head(tail(expLst)).tag === 2) {
                    if (head(tail(expLst)).fields[0].tag === 0) {
                        matchResult_1 = 4;
                        exp_2 = head(expLst);
                        expN_1 = head(tail(expLst)).fields[1];
                        tl = tail(tail(expLst));
                    }
                    else {
                        matchResult_1 = 5;
                        exp1_1 = head(expLst);
                        exp2_1 = head(tail(expLst));
                        tl_1 = tail(tail(expLst));
                    }
                }
                else {
                    matchResult_1 = 5;
                    exp1_1 = head(expLst);
                    exp2_1 = head(tail(expLst));
                    tl_1 = tail(tail(expLst));
                }
            }
            else if (head(tail(expLst)).tag === 2) {
                if (head(tail(expLst)).fields[0].tag === 0) {
                    matchResult_1 = 4;
                    exp_2 = head(expLst);
                    expN_1 = head(tail(expLst)).fields[1];
                    tl = tail(tail(expLst));
                }
                else {
                    matchResult_1 = 5;
                    exp1_1 = head(expLst);
                    exp2_1 = head(tail(expLst));
                    tl_1 = tail(tail(expLst));
                }
            }
            else {
                matchResult_1 = 5;
                exp1_1 = head(expLst);
                exp2_1 = head(tail(expLst));
                tl_1 = tail(tail(expLst));
            }
        }
        else {
            matchResult_1 = 1;
            exp = head(expLst);
        }
    }
    else {
        matchResult_1 = 0;
    }
    switch (matchResult_1) {
        case 0:
            return new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* Word */ 0, [0]), width)]);
        case 1:
            return exp;
        case 2:
            return new FastAlgExp(/* BinaryExp */ 3, [exp_1, BinaryOp.SubOp, expN]);
        case 3:
            return new FastAlgExp(/* BinaryExp */ 3, [exp1, BinaryOp.AddOp, exp2]);
        case 4:
            return fold_1(assemble, new FastAlgExp(/* BinaryExp */ 3, [exp_2, BinaryOp.SubOp, expN_1]), tl);
        default:
            return fold_1(assemble, new FastAlgExp(/* BinaryExp */ 3, [exp1_1, BinaryOp.AddOp, exp2_1]), tl_1);
    }
}

export function tryBitwiseOperation(expressions) {
    let matchResult, bop, left, right;
    if (!isEmpty_1(expressions)) {
        if (head(expressions).tag === 3) {
            switch (head(expressions).fields[1].tag) {
                case 0:
                case 1: {
                    matchResult = 1;
                    break;
                }
                default:
                    if (head(expressions).fields[0].tag === 2) {
                        if (head(expressions).fields[0].fields[0].tag === 2) {
                            if (head(expressions).fields[2].tag === 2) {
                                if (head(expressions).fields[2].fields[0].tag === 2) {
                                    matchResult = 2;
                                    bop = head(expressions).fields[1];
                                    left = head(expressions).fields[0].fields[1];
                                    right = head(expressions).fields[2].fields[1];
                                }
                                else {
                                    matchResult = 3;
                                }
                            }
                            else {
                                matchResult = 3;
                            }
                        }
                        else {
                            matchResult = 3;
                        }
                    }
                    else {
                        matchResult = 3;
                    }
            }
        }
        else {
            matchResult = 3;
        }
    }
    else {
        matchResult = 0;
    }
    switch (matchResult) {
        case 0:
            return toFail(printf("what? Expressions List should never be empty"));
        case 1:
            return undefined;
        case 2: {
            const widthL = getAlgExpWidth(left) | 0;
            if ((widthL !== getAlgExpWidth(right)) ? true : (length(expressions) !== widthL)) {
                return undefined;
            }
            else if (forAll((tupledArg) => {
                const exp_1 = tupledArg[1];
                let matchResult_1, l, ll, lu, op, r, rl, ru;
                if (exp_1.tag === 3) {
                    if (exp_1.fields[0].tag === 2) {
                        if (exp_1.fields[0].fields[0].tag === 2) {
                            if (exp_1.fields[2].tag === 2) {
                                if (exp_1.fields[2].fields[0].tag === 2) {
                                    matchResult_1 = 0;
                                    l = exp_1.fields[0].fields[1];
                                    ll = exp_1.fields[0].fields[0].fields[0];
                                    lu = exp_1.fields[0].fields[0].fields[1];
                                    op = exp_1.fields[1];
                                    r = exp_1.fields[2].fields[1];
                                    rl = exp_1.fields[2].fields[0].fields[0];
                                    ru = exp_1.fields[2].fields[0].fields[1];
                                }
                                else {
                                    matchResult_1 = 1;
                                }
                            }
                            else {
                                matchResult_1 = 1;
                            }
                        }
                        else {
                            matchResult_1 = 1;
                        }
                    }
                    else {
                        matchResult_1 = 1;
                    }
                }
                else {
                    matchResult_1 = 1;
                }
                switch (matchResult_1) {
                    case 0:
                        if ((((((ll === lu) && (ll === rl)) && (rl === ru)) && (ll === tupledArg[0])) && equals(l, left)) && equals(r, right)) {
                            return equals(op, bop);
                        }
                        else {
                            return false;
                        }
                    default:
                        return false;
                }
            }, mapIndexed((i, exp) => [(widthL - 1) - i, exp], expressions))) {
                return new FastAlgExp(/* BinaryExp */ 3, [left, bop, right]);
            }
            else {
                return undefined;
            }
        }
        default:
            return undefined;
    }
}

/**
 * Check the Bit Ranges for two expressions, and check if they can be merged.
 * The first range must be the MORE significant part, since AppendExp lists are MSB-first:
 */
export function tryMergeBitRanges(l1, u1, exp1, l2, u2, exp2) {
    if (equals(exp1, exp2) && (l1 === (u2 + 1))) {
        return new FastAlgExp(/* UnaryExp */ 2, [new UnaryOp(/* BitRangeOp */ 2, [l2, u1]), exp1]);
    }
    else {
        return undefined;
    }
}

export function foldAppends(expressions) {
    return reverse(fold_1((acc, exp) => {
        let matchResult, e, exp0, exp1, l1, l2, tl, u1, u2;
        if (!isEmpty_1(acc)) {
            if (head(acc).tag === 2) {
                if (head(acc).fields[0].tag === 2) {
                    if (exp.tag === 2) {
                        if (exp.fields[0].tag === 2) {
                            matchResult = 1;
                            exp0 = head(acc).fields[1];
                            exp1 = exp.fields[1];
                            l1 = head(acc).fields[0].fields[0];
                            l2 = exp.fields[0].fields[0];
                            tl = tail(acc);
                            u1 = head(acc).fields[0].fields[1];
                            u2 = exp.fields[0].fields[1];
                        }
                        else {
                            matchResult = 2;
                        }
                    }
                    else {
                        matchResult = 2;
                    }
                }
                else {
                    matchResult = 2;
                }
            }
            else {
                matchResult = 2;
            }
        }
        else {
            matchResult = 0;
            e = exp;
        }
        switch (matchResult) {
            case 0:
                return cons(exp, acc);
            case 1: {
                const matchValue_1 = tryMergeBitRanges(l1, u1, exp0, l2, u2, exp1);
                if (matchValue_1 == null) {
                    return cons(exp, acc);
                }
                else {
                    return cons(matchValue_1, tl);
                }
            }
            default:
                return cons(exp, acc);
        }
    }, empty_1(), expressions));
}

/**
 * Converts an Algebraic Expression to a string for pretty printing
 * This function is now used for debugging purposes, if Katex doesn't work as expected, we can use this function again
 * by changing the name to expToKatex. -- 13/1/2025
 */
export function expToKatex(exp) {
    const expToKatex$0027 = (exp_1) => {
        let matchResult, label, w, w_1, e, e_1, e_2, low, up, e_3;
        switch (exp_1.tag) {
            case 0: {
                matchResult = 0;
                label = exp_1.fields[0][1];
                break;
            }
            case 1: {
                if (exp_1.fields[0].Dat.tag === 1) {
                    matchResult = 2;
                    w_1 = exp_1.fields[0].Dat.fields[0];
                }
                else {
                    matchResult = 1;
                    w = exp_1.fields[0].Dat.fields[0];
                }
                break;
            }
            case 2: {
                switch (exp_1.fields[0].tag) {
                    case 1: {
                        matchResult = 4;
                        e_1 = exp_1.fields[1];
                        break;
                    }
                    case 2: {
                        matchResult = 5;
                        e_2 = exp_1.fields[1];
                        low = exp_1.fields[0].fields[0];
                        up = exp_1.fields[0].fields[1];
                        break;
                    }
                    case 3: {
                        matchResult = 6;
                        e_3 = exp_1.fields[1];
                        break;
                    }
                    default: {
                        matchResult = 3;
                        e = exp_1.fields[1];
                    }
                }
                break;
            }
            case 3: {
                switch (exp_1.fields[1].tag) {
                    case 0: {
                        if (getAlgExpWidth(exp_1) === 1) {
                            matchResult = 7;
                        }
                        else {
                            matchResult = 8;
                        }
                        break;
                    }
                    case 1: {
                        if (getAlgExpWidth(exp_1) === 1) {
                            matchResult = 7;
                        }
                        else {
                            matchResult = 8;
                        }
                        break;
                    }
                    default:
                        matchResult = 8;
                }
                break;
            }
            default:
                matchResult = 8;
        }
        switch (matchResult) {
            case 0:
                return label;
            case 1:
                return w.toString();
            case 2:
                return toString(w_1);
            case 3: {
                const arg = expToKatex$0027(e);
                return toText(printf("(-%s)"))(arg);
            }
            case 4: {
                const arg_1 = expToKatex$0027(e_1);
                return toText(printf("\\overline{%s}"))(arg_1);
            }
            case 5: {
                const baseStr = expToKatex$0027(e_2);
                if (low === up) {
                    return toText(printf("%s_{[%d]}"))(baseStr)(up);
                }
                else {
                    return toText(printf("%s_{[%d:%d]}"))(baseStr)(up)(low);
                }
            }
            case 6: {
                const arg_7 = expToKatex$0027(e_3);
                return toText(printf("\\mathrm{carry}\\bigl(%s\\bigr)"))(arg_7);
            }
            case 7: {
                const arg_8 = join(" \\oplus ", map((_arg) => {
                    let matchResult_1, e_4, e_5;
                    if (_arg.tag === 2) {
                        if (_arg.fields[0].tag === 0) {
                            matchResult_1 = 0;
                            e_4 = _arg.fields[1];
                        }
                        else {
                            matchResult_1 = 1;
                            e_5 = _arg;
                        }
                    }
                    else {
                        matchResult_1 = 1;
                        e_5 = _arg;
                    }
                    switch (matchResult_1) {
                        case 0:
                            return expToKatex$0027(e_4);
                        default:
                            return expToKatex$0027(e_5);
                    }
                }, flattenNestedArithmetic(exp_1)));
                return toText(printf("\\bigl(%s\\bigr)"))(arg_8);
            }
            default: {
                let matchResult_2, e1, e2, e1_1, e2_1, e1_2, e2_2, e_6, x, exps;
                switch (exp_1.tag) {
                    case 3: {
                        switch (exp_1.fields[1].tag) {
                            case 2: {
                                matchResult_2 = 1;
                                e1 = exp_1.fields[0];
                                e2 = exp_1.fields[2];
                                break;
                            }
                            case 3: {
                                matchResult_2 = 2;
                                e1_1 = exp_1.fields[0];
                                e2_1 = exp_1.fields[2];
                                break;
                            }
                            case 4: {
                                matchResult_2 = 3;
                                e1_2 = exp_1.fields[0];
                                e2_2 = exp_1.fields[2];
                                break;
                            }
                            default:
                                matchResult_2 = 0;
                        }
                        break;
                    }
                    case 4: {
                        matchResult_2 = 4;
                        e_6 = exp_1.fields[0];
                        x = exp_1.fields[2];
                        break;
                    }
                    case 5: {
                        matchResult_2 = 5;
                        exps = exp_1.fields[0];
                        break;
                    }
                    default:
                        matchResult_2 = 6;
                }
                switch (matchResult_2) {
                    case 0: {
                        const arg_9 = arithmeticToKatex(exp_1);
                        return toText(printf("\\bigl(%s\\bigr)"))(arg_9);
                    }
                    case 1: {
                        const arg_10 = expToKatex$0027(e1);
                        const arg_11 = expToKatex$0027(e2);
                        return toText(printf("%s \\cdot %s"))(arg_10)(arg_11);
                    }
                    case 2: {
                        const arg_12 = expToKatex$0027(e1_1);
                        const arg_13 = expToKatex$0027(e2_1);
                        return toText(printf("%s + %s"))(arg_12)(arg_13);
                    }
                    case 3: {
                        const arg_14 = expToKatex$0027(e1_2);
                        const arg_15 = expToKatex$0027(e2_2);
                        return toText(printf("%s \\oplus %s"))(arg_14)(arg_15);
                    }
                    case 4: {
                        const arg_16 = expToKatex$0027(e_6);
                        const arg_17 = toString(x);
                        return toText(printf("\\bigl(%s = %s\\bigr)"))(arg_16)(arg_17);
                    }
                    case 5: {
                        const arg_18 = join("\\Vert ", map(expToKatex$0027, exps));
                        return toText(printf("\\bigl(%s\\bigr)"))(arg_18);
                    }
                    default:
                        throw new Exception("Match failure: SimGraphTypes.FastAlgExp");
                }
            }
        }
    };
    const arithmeticToKatex = (exp_3) => join(" ", mapIndexed((i, expr) => {
        let matchResult_3, e_7, e_8, e_9;
        if (i === 0) {
            matchResult_3 = 0;
            e_7 = expr;
        }
        else if (expr.tag === 2) {
            if (expr.fields[0].tag === 0) {
                matchResult_3 = 1;
                e_8 = expr.fields[1];
            }
            else {
                matchResult_3 = 2;
                e_9 = expr;
            }
        }
        else {
            matchResult_3 = 2;
            e_9 = expr;
        }
        switch (matchResult_3) {
            case 0:
                return expToKatex$0027(e_7);
            case 1: {
                const arg_19 = expToKatex$0027(e_8);
                return toText(printf("- %s"))(arg_19);
            }
            default: {
                const arg_20 = expToKatex$0027(e_9);
                return toText(printf("+ %s"))(arg_20);
            }
        }
    }, flattenNestedArithmetic(exp_3)));
    const katexStr = expToKatex$0027(exp);
    let matchResult_4;
    switch (exp.tag) {
        case 4:
        case 5: {
            matchResult_4 = 0;
            break;
        }
        case 3: {
            switch (exp.fields[1].tag) {
                case 0:
                case 1: {
                    matchResult_4 = 0;
                    break;
                }
                default:
                    matchResult_4 = 2;
            }
            break;
        }
        case 2: {
            if (exp.fields[0].tag === 0) {
                matchResult_4 = 1;
            }
            else {
                matchResult_4 = 2;
            }
            break;
        }
        default:
            matchResult_4 = 2;
    }
    switch (matchResult_4) {
        case 0:
            return katexStr.slice("\\bigl(".length, ((katexStr.length - "\\bigr)".length) - 1) + 1);
        case 1:
            return katexStr.slice(1, (katexStr.length - 2) + 1);
        default:
            return katexStr;
    }
}

/**
 * Recursively evaluates an expression to reduce it to its simplest form
 */
export function evalExp(exp_mut) {
    evalExp:
    while (true) {
        const exp = exp_mut;
        let matchResult, exp_1, exp_2, exp_3, op, exp1, exp2, exp1_1, exp2_1, exp1_2, exp2_2, exp_24, x, exps;
        switch (exp.tag) {
            case 1: {
                matchResult = 1;
                break;
            }
            case 2: {
                switch (exp.fields[0].tag) {
                    case 1: {
                        matchResult = 2;
                        exp_1 = exp.fields[1];
                        break;
                    }
                    case 0: {
                        if (exp.fields[1].tag === 2) {
                            if (exp.fields[1].fields[0].tag === 0) {
                                matchResult = 3;
                                exp_2 = exp.fields[1].fields[1];
                            }
                            else {
                                matchResult = 4;
                                exp_3 = exp.fields[1];
                                op = exp.fields[0];
                            }
                        }
                        else {
                            matchResult = 4;
                            exp_3 = exp.fields[1];
                            op = exp.fields[0];
                        }
                        break;
                    }
                    default: {
                        matchResult = 4;
                        exp_3 = exp.fields[1];
                        op = exp.fields[0];
                    }
                }
                break;
            }
            case 3: {
                switch (exp.fields[1].tag) {
                    case 0:
                    case 1: {
                        matchResult = 8;
                        break;
                    }
                    case 3: {
                        matchResult = 6;
                        exp1_1 = exp.fields[0];
                        exp2_1 = exp.fields[2];
                        break;
                    }
                    case 4: {
                        matchResult = 7;
                        exp1_2 = exp.fields[0];
                        exp2_2 = exp.fields[2];
                        break;
                    }
                    default: {
                        matchResult = 5;
                        exp1 = exp.fields[0];
                        exp2 = exp.fields[2];
                    }
                }
                break;
            }
            case 4: {
                matchResult = 9;
                exp_24 = exp.fields[0];
                x = exp.fields[2];
                break;
            }
            case 5: {
                matchResult = 10;
                exps = exp.fields[0];
                break;
            }
            default:
                matchResult = 0;
        }
        switch (matchResult) {
            case 0:
                return exp;
            case 1:
                return exp;
            case 2: {
                const matchValue = evalExp(exp_1);
                let matchResult_1, inner;
                if (matchValue.tag === 2) {
                    if (matchValue.fields[0].tag === 1) {
                        matchResult_1 = 0;
                        inner = matchValue.fields[1];
                    }
                    else {
                        matchResult_1 = 1;
                    }
                }
                else {
                    matchResult_1 = 1;
                }
                switch (matchResult_1) {
                    case 0: {
                        exp_mut = inner;
                        continue evalExp;
                    }
                    default:
                        return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NotOp, evalExp(exp_1)]);
                }
            }
            case 3: {
                const matchValue_1 = evalExp(exp_2);
                let matchResult_2, inner_1;
                if (matchValue_1.tag === 2) {
                    if (matchValue_1.fields[0].tag === 0) {
                        matchResult_2 = 0;
                        inner_1 = matchValue_1.fields[1];
                    }
                    else {
                        matchResult_2 = 1;
                    }
                }
                else {
                    matchResult_2 = 1;
                }
                switch (matchResult_2) {
                    case 0: {
                        exp_mut = inner_1;
                        continue evalExp;
                    }
                    default:
                        return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NegOp, evalExp(exp_2)]);
                }
            }
            case 4:
                return new FastAlgExp(/* UnaryExp */ 2, [op, evalExp(exp_3)]);
            case 5: {
                const left = evalExp(exp1);
                const right = evalExp(exp2);
                let matchResult_3, w_6, w_7, z_6;
                if (right.tag === 1) {
                    if (right.fields[0].Dat.tag === 1) {
                        if (left.tag === 1) {
                            if (left.fields[0].Dat.tag === 1) {
                                if (equals_1(right.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_3 = 1;
                                    w_7 = right.fields[0].Width;
                                    z_6 = right.fields[0].Dat.fields[0];
                                }
                                else if (equals_1(left.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_3 = 1;
                                    w_7 = left.fields[0].Width;
                                    z_6 = left.fields[0].Dat.fields[0];
                                }
                                else {
                                    matchResult_3 = 2;
                                }
                            }
                            else if (left.fields[0].Dat.fields[0] === 0) {
                                matchResult_3 = 0;
                                w_6 = left.fields[0].Width;
                            }
                            else if (equals_1(right.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_3 = 1;
                                w_7 = right.fields[0].Width;
                                z_6 = right.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_3 = 2;
                            }
                        }
                        else if (equals_1(right.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_3 = 1;
                            w_7 = right.fields[0].Width;
                            z_6 = right.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_3 = 2;
                        }
                    }
                    else if (right.fields[0].Dat.fields[0] === 0) {
                        matchResult_3 = 0;
                        w_6 = right.fields[0].Width;
                    }
                    else if (left.tag === 1) {
                        if (left.fields[0].Dat.tag === 1) {
                            if (equals_1(left.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_3 = 1;
                                w_7 = left.fields[0].Width;
                                z_6 = left.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_3 = 2;
                            }
                        }
                        else if (left.fields[0].Dat.fields[0] === 0) {
                            matchResult_3 = 0;
                            w_6 = left.fields[0].Width;
                        }
                        else {
                            matchResult_3 = 2;
                        }
                    }
                    else {
                        matchResult_3 = 2;
                    }
                }
                else if (left.tag === 1) {
                    if (left.fields[0].Dat.tag === 1) {
                        if (equals_1(left.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_3 = 1;
                            w_7 = left.fields[0].Width;
                            z_6 = left.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_3 = 2;
                        }
                    }
                    else if (left.fields[0].Dat.fields[0] === 0) {
                        matchResult_3 = 0;
                        w_6 = left.fields[0].Width;
                    }
                    else {
                        matchResult_3 = 2;
                    }
                }
                else {
                    matchResult_3 = 2;
                }
                switch (matchResult_3) {
                    case 0:
                        return new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* Word */ 0, [0]), w_6)]);
                    case 1:
                        return zeroLiteral(w_7);
                    default: {
                        let matchResult_4, exp_4, n, w_8, exp_5, n_1, w_9, e1_5, e2_5;
                        switch (right.tag) {
                            case 1: {
                                if (right.fields[0].Dat.tag === 1) {
                                    switch (left.tag) {
                                        case 1: {
                                            if (left.fields[0].Dat.tag === 1) {
                                                matchResult_4 = 1;
                                                exp_5 = left;
                                                n_1 = right.fields[0].Dat.fields[0];
                                                w_9 = right.fields[0].Width;
                                            }
                                            else {
                                                matchResult_4 = 0;
                                                exp_4 = right;
                                                n = left.fields[0].Dat.fields[0];
                                                w_8 = left.fields[0].Width;
                                            }
                                            break;
                                        }
                                        case 2: {
                                            matchResult_4 = 1;
                                            exp_5 = left;
                                            n_1 = right.fields[0].Dat.fields[0];
                                            w_9 = right.fields[0].Width;
                                            break;
                                        }
                                        default: {
                                            matchResult_4 = 1;
                                            exp_5 = left;
                                            n_1 = right.fields[0].Dat.fields[0];
                                            w_9 = right.fields[0].Width;
                                        }
                                    }
                                }
                                else {
                                    matchResult_4 = 0;
                                    exp_4 = left;
                                    n = right.fields[0].Dat.fields[0];
                                    w_8 = right.fields[0].Width;
                                }
                                break;
                            }
                            case 2: {
                                switch (left.tag) {
                                    case 1: {
                                        if (left.fields[0].Dat.tag === 1) {
                                            matchResult_4 = 1;
                                            exp_5 = right;
                                            n_1 = left.fields[0].Dat.fields[0];
                                            w_9 = left.fields[0].Width;
                                        }
                                        else {
                                            matchResult_4 = 0;
                                            exp_4 = right;
                                            n = left.fields[0].Dat.fields[0];
                                            w_8 = left.fields[0].Width;
                                        }
                                        break;
                                    }
                                    case 2: {
                                        if (right.fields[0].tag === 1) {
                                            if (equals(left, right.fields[1])) {
                                                matchResult_4 = 2;
                                                e1_5 = left;
                                                e2_5 = right.fields[1];
                                            }
                                            else if (left.fields[0].tag === 1) {
                                                if (equals(right, left.fields[1])) {
                                                    matchResult_4 = 2;
                                                    e1_5 = right;
                                                    e2_5 = left.fields[1];
                                                }
                                                else {
                                                    matchResult_4 = 3;
                                                }
                                            }
                                            else {
                                                matchResult_4 = 3;
                                            }
                                        }
                                        else if (left.fields[0].tag === 1) {
                                            if (equals(right, left.fields[1])) {
                                                matchResult_4 = 2;
                                                e1_5 = right;
                                                e2_5 = left.fields[1];
                                            }
                                            else {
                                                matchResult_4 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_4 = 3;
                                        }
                                        break;
                                    }
                                    default:
                                        if (right.fields[0].tag === 1) {
                                            if (equals(left, right.fields[1])) {
                                                matchResult_4 = 2;
                                                e1_5 = left;
                                                e2_5 = right.fields[1];
                                            }
                                            else {
                                                matchResult_4 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_4 = 3;
                                        }
                                }
                                break;
                            }
                            default:
                                switch (left.tag) {
                                    case 1: {
                                        if (left.fields[0].Dat.tag === 1) {
                                            matchResult_4 = 1;
                                            exp_5 = right;
                                            n_1 = left.fields[0].Dat.fields[0];
                                            w_9 = left.fields[0].Width;
                                        }
                                        else {
                                            matchResult_4 = 0;
                                            exp_4 = right;
                                            n = left.fields[0].Dat.fields[0];
                                            w_8 = left.fields[0].Width;
                                        }
                                        break;
                                    }
                                    case 2: {
                                        if (left.fields[0].tag === 1) {
                                            if (equals(right, left.fields[1])) {
                                                matchResult_4 = 2;
                                                e1_5 = right;
                                                e2_5 = left.fields[1];
                                            }
                                            else {
                                                matchResult_4 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_4 = 3;
                                        }
                                        break;
                                    }
                                    default:
                                        matchResult_4 = 3;
                                }
                        }
                        switch (matchResult_4) {
                            case 0:
                                if (n === (toUInt32(bigIntMask(w_8)) >>> 0)) {
                                    return exp_4;
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left, BinaryOp.BitAndOp, right]);
                                }
                            case 1:
                                if (equals_1(n_1, bigIntMask(w_9))) {
                                    return exp_5;
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left, BinaryOp.BitAndOp, right]);
                                }
                            case 2:
                                return zeroLiteral(getAlgExpWidth(e1_5));
                            default: {
                                let matchResult_5, e1_6, e2_6, e3, e4, l, r;
                                if (left.tag === 3) {
                                    if (left.fields[1].tag === 3) {
                                        if (right.tag === 3) {
                                            if (right.fields[1].tag === 3) {
                                                matchResult_5 = 0;
                                                e1_6 = left.fields[0];
                                                e2_6 = left.fields[2];
                                                e3 = right.fields[0];
                                                e4 = right.fields[2];
                                            }
                                            else {
                                                matchResult_5 = 1;
                                                l = left;
                                                r = right;
                                            }
                                        }
                                        else {
                                            matchResult_5 = 1;
                                            l = left;
                                            r = right;
                                        }
                                    }
                                    else {
                                        matchResult_5 = 1;
                                        l = left;
                                        r = right;
                                    }
                                }
                                else {
                                    matchResult_5 = 1;
                                    l = left;
                                    r = right;
                                }
                                switch (matchResult_5) {
                                    case 0:
                                        if (equals(e1_6, e3)) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e1_6, BinaryOp.BitOrOp, new FastAlgExp(/* BinaryExp */ 3, [e2_6, BinaryOp.BitAndOp, e4])]);
                                        }
                                        else if (equals(e1_6, e4)) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e1_6, BinaryOp.BitOrOp, new FastAlgExp(/* BinaryExp */ 3, [e2_6, BinaryOp.BitAndOp, e3])]);
                                        }
                                        else if (equals(e2_6, e3)) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e2_6, BinaryOp.BitOrOp, new FastAlgExp(/* BinaryExp */ 3, [e1_6, BinaryOp.BitAndOp, e4])]);
                                        }
                                        else if (equals(e2_6, e4)) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e2_6, BinaryOp.BitOrOp, new FastAlgExp(/* BinaryExp */ 3, [e1_6, BinaryOp.BitAndOp, e3])]);
                                        }
                                        else {
                                            return new FastAlgExp(/* BinaryExp */ 3, [left, BinaryOp.BitAndOp, right]);
                                        }
                                    default:
                                        if (equals(l, r)) {
                                            return l;
                                        }
                                        else {
                                            return new FastAlgExp(/* BinaryExp */ 3, [l, BinaryOp.BitAndOp, r]);
                                        }
                                }
                            }
                        }
                    }
                }
            }
            case 6: {
                const left_1 = evalExp(exp1_1);
                const right_1 = evalExp(exp2_1);
                let matchResult_6, exp_12, exp_13, z_13;
                if (right_1.tag === 1) {
                    if (right_1.fields[0].Dat.tag === 1) {
                        if (left_1.tag === 1) {
                            if (left_1.fields[0].Dat.tag === 1) {
                                if (equals_1(right_1.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_6 = 1;
                                    exp_13 = left_1;
                                    z_13 = right_1.fields[0].Dat.fields[0];
                                }
                                else if (equals_1(left_1.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_6 = 1;
                                    exp_13 = right_1;
                                    z_13 = left_1.fields[0].Dat.fields[0];
                                }
                                else {
                                    matchResult_6 = 2;
                                }
                            }
                            else if (left_1.fields[0].Dat.fields[0] === 0) {
                                matchResult_6 = 0;
                                exp_12 = right_1;
                            }
                            else if (equals_1(right_1.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_6 = 1;
                                exp_13 = left_1;
                                z_13 = right_1.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_6 = 2;
                            }
                        }
                        else if (equals_1(right_1.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_6 = 1;
                            exp_13 = left_1;
                            z_13 = right_1.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_6 = 2;
                        }
                    }
                    else if (right_1.fields[0].Dat.fields[0] === 0) {
                        matchResult_6 = 0;
                        exp_12 = left_1;
                    }
                    else if (left_1.tag === 1) {
                        if (left_1.fields[0].Dat.tag === 1) {
                            if (equals_1(left_1.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_6 = 1;
                                exp_13 = right_1;
                                z_13 = left_1.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_6 = 2;
                            }
                        }
                        else if (left_1.fields[0].Dat.fields[0] === 0) {
                            matchResult_6 = 0;
                            exp_12 = right_1;
                        }
                        else {
                            matchResult_6 = 2;
                        }
                    }
                    else {
                        matchResult_6 = 2;
                    }
                }
                else if (left_1.tag === 1) {
                    if (left_1.fields[0].Dat.tag === 1) {
                        if (equals_1(left_1.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_6 = 1;
                            exp_13 = right_1;
                            z_13 = left_1.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_6 = 2;
                        }
                    }
                    else if (left_1.fields[0].Dat.fields[0] === 0) {
                        matchResult_6 = 0;
                        exp_12 = right_1;
                    }
                    else {
                        matchResult_6 = 2;
                    }
                }
                else {
                    matchResult_6 = 2;
                }
                switch (matchResult_6) {
                    case 0:
                        return exp_12;
                    case 1:
                        return exp_13;
                    default: {
                        let matchResult_7, n_2, w_10, n_3, w_11, e1_12, e2_12;
                        switch (right_1.tag) {
                            case 1: {
                                if (right_1.fields[0].Dat.tag === 1) {
                                    switch (left_1.tag) {
                                        case 1: {
                                            if (left_1.fields[0].Dat.tag === 1) {
                                                matchResult_7 = 1;
                                                n_3 = right_1.fields[0].Dat.fields[0];
                                                w_11 = right_1.fields[0].Width;
                                            }
                                            else {
                                                matchResult_7 = 0;
                                                n_2 = left_1.fields[0].Dat.fields[0];
                                                w_10 = left_1.fields[0].Width;
                                            }
                                            break;
                                        }
                                        case 2: {
                                            matchResult_7 = 1;
                                            n_3 = right_1.fields[0].Dat.fields[0];
                                            w_11 = right_1.fields[0].Width;
                                            break;
                                        }
                                        default: {
                                            matchResult_7 = 1;
                                            n_3 = right_1.fields[0].Dat.fields[0];
                                            w_11 = right_1.fields[0].Width;
                                        }
                                    }
                                }
                                else {
                                    matchResult_7 = 0;
                                    n_2 = right_1.fields[0].Dat.fields[0];
                                    w_10 = right_1.fields[0].Width;
                                }
                                break;
                            }
                            case 2: {
                                switch (left_1.tag) {
                                    case 1: {
                                        if (left_1.fields[0].Dat.tag === 1) {
                                            matchResult_7 = 1;
                                            n_3 = left_1.fields[0].Dat.fields[0];
                                            w_11 = left_1.fields[0].Width;
                                        }
                                        else {
                                            matchResult_7 = 0;
                                            n_2 = left_1.fields[0].Dat.fields[0];
                                            w_10 = left_1.fields[0].Width;
                                        }
                                        break;
                                    }
                                    case 2: {
                                        if (right_1.fields[0].tag === 1) {
                                            if (equals(left_1, right_1.fields[1])) {
                                                matchResult_7 = 2;
                                                e1_12 = left_1;
                                                e2_12 = right_1.fields[1];
                                            }
                                            else if (left_1.fields[0].tag === 1) {
                                                if (equals(right_1, left_1.fields[1])) {
                                                    matchResult_7 = 2;
                                                    e1_12 = right_1;
                                                    e2_12 = left_1.fields[1];
                                                }
                                                else {
                                                    matchResult_7 = 3;
                                                }
                                            }
                                            else {
                                                matchResult_7 = 3;
                                            }
                                        }
                                        else if (left_1.fields[0].tag === 1) {
                                            if (equals(right_1, left_1.fields[1])) {
                                                matchResult_7 = 2;
                                                e1_12 = right_1;
                                                e2_12 = left_1.fields[1];
                                            }
                                            else {
                                                matchResult_7 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_7 = 3;
                                        }
                                        break;
                                    }
                                    default:
                                        if (right_1.fields[0].tag === 1) {
                                            if (equals(left_1, right_1.fields[1])) {
                                                matchResult_7 = 2;
                                                e1_12 = left_1;
                                                e2_12 = right_1.fields[1];
                                            }
                                            else {
                                                matchResult_7 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_7 = 3;
                                        }
                                }
                                break;
                            }
                            default:
                                switch (left_1.tag) {
                                    case 1: {
                                        if (left_1.fields[0].Dat.tag === 1) {
                                            matchResult_7 = 1;
                                            n_3 = left_1.fields[0].Dat.fields[0];
                                            w_11 = left_1.fields[0].Width;
                                        }
                                        else {
                                            matchResult_7 = 0;
                                            n_2 = left_1.fields[0].Dat.fields[0];
                                            w_10 = left_1.fields[0].Width;
                                        }
                                        break;
                                    }
                                    case 2: {
                                        if (left_1.fields[0].tag === 1) {
                                            if (equals(right_1, left_1.fields[1])) {
                                                matchResult_7 = 2;
                                                e1_12 = right_1;
                                                e2_12 = left_1.fields[1];
                                            }
                                            else {
                                                matchResult_7 = 3;
                                            }
                                        }
                                        else {
                                            matchResult_7 = 3;
                                        }
                                        break;
                                    }
                                    default:
                                        matchResult_7 = 3;
                                }
                        }
                        switch (matchResult_7) {
                            case 0:
                                if (n_2 === (toUInt32(bigIntMask(w_10)) >>> 0)) {
                                    return new FastAlgExp(/* DataLiteral */ 1, [new FastData(new FastBits(/* Word */ 0, [n_2]), w_10)]);
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left_1, BinaryOp.BitOrOp, right_1]);
                                }
                            case 1:
                                if (equals_1(n_3, bigIntMask(w_11))) {
                                    return allOnesLiteral(w_11);
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left_1, BinaryOp.BitOrOp, right_1]);
                                }
                            case 2:
                                return allOnesLiteral(getAlgExpWidth(e1_12));
                            default: {
                                let matchResult_8, a1, a2, b1, b2, c1, e1_13, e2_13, e3_1, l_1, r_1;
                                if (left_1.tag === 3) {
                                    if (left_1.fields[1].tag === 2) {
                                        if (left_1.fields[2].tag === 3) {
                                            if (left_1.fields[2].fields[1].tag === 0) {
                                                if (right_1.tag === 3) {
                                                    if (right_1.fields[1].tag === 2) {
                                                        matchResult_8 = 0;
                                                        a1 = left_1.fields[2].fields[0];
                                                        a2 = right_1.fields[0];
                                                        b1 = left_1.fields[2].fields[2];
                                                        b2 = right_1.fields[2];
                                                        c1 = left_1.fields[0];
                                                    }
                                                    else {
                                                        matchResult_8 = 1;
                                                        e1_13 = right_1;
                                                        e2_13 = left_1.fields[0];
                                                        e3_1 = left_1.fields[2];
                                                    }
                                                }
                                                else {
                                                    matchResult_8 = 1;
                                                    e1_13 = right_1;
                                                    e2_13 = left_1.fields[0];
                                                    e3_1 = left_1.fields[2];
                                                }
                                            }
                                            else if (right_1.tag === 3) {
                                                if (right_1.fields[1].tag === 2) {
                                                    if (right_1.fields[2].tag === 3) {
                                                        if (right_1.fields[2].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = right_1.fields[2].fields[0];
                                                            a2 = left_1.fields[0];
                                                            b1 = right_1.fields[2].fields[2];
                                                            b2 = left_1.fields[2];
                                                            c1 = right_1.fields[0];
                                                        }
                                                        else if (left_1.fields[0].tag === 3) {
                                                            if (left_1.fields[0].fields[1].tag === 0) {
                                                                matchResult_8 = 0;
                                                                a1 = left_1.fields[0].fields[0];
                                                                a2 = right_1.fields[0];
                                                                b1 = left_1.fields[0].fields[2];
                                                                b2 = right_1.fields[2];
                                                                c1 = left_1.fields[2];
                                                            }
                                                            else if (right_1.fields[0].tag === 3) {
                                                                if (right_1.fields[0].fields[1].tag === 0) {
                                                                    matchResult_8 = 0;
                                                                    a1 = right_1.fields[0].fields[0];
                                                                    a2 = left_1.fields[0];
                                                                    b1 = right_1.fields[0].fields[2];
                                                                    b2 = left_1.fields[2];
                                                                    c1 = right_1.fields[2];
                                                                }
                                                                else {
                                                                    matchResult_8 = 1;
                                                                    e1_13 = left_1;
                                                                    e2_13 = right_1.fields[0];
                                                                    e3_1 = right_1.fields[2];
                                                                }
                                                            }
                                                            else {
                                                                matchResult_8 = 1;
                                                                e1_13 = left_1;
                                                                e2_13 = right_1.fields[0];
                                                                e3_1 = right_1.fields[2];
                                                            }
                                                        }
                                                        else if (right_1.fields[0].tag === 3) {
                                                            if (right_1.fields[0].fields[1].tag === 0) {
                                                                matchResult_8 = 0;
                                                                a1 = right_1.fields[0].fields[0];
                                                                a2 = left_1.fields[0];
                                                                b1 = right_1.fields[0].fields[2];
                                                                b2 = left_1.fields[2];
                                                                c1 = right_1.fields[2];
                                                            }
                                                            else {
                                                                matchResult_8 = 1;
                                                                e1_13 = left_1;
                                                                e2_13 = right_1.fields[0];
                                                                e3_1 = right_1.fields[2];
                                                            }
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else if (left_1.fields[0].tag === 3) {
                                                        if (left_1.fields[0].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = left_1.fields[0].fields[0];
                                                            a2 = right_1.fields[0];
                                                            b1 = left_1.fields[0].fields[2];
                                                            b2 = right_1.fields[2];
                                                            c1 = left_1.fields[2];
                                                        }
                                                        else if (right_1.fields[0].tag === 3) {
                                                            if (right_1.fields[0].fields[1].tag === 0) {
                                                                matchResult_8 = 0;
                                                                a1 = right_1.fields[0].fields[0];
                                                                a2 = left_1.fields[0];
                                                                b1 = right_1.fields[0].fields[2];
                                                                b2 = left_1.fields[2];
                                                                c1 = right_1.fields[2];
                                                            }
                                                            else {
                                                                matchResult_8 = 1;
                                                                e1_13 = left_1;
                                                                e2_13 = right_1.fields[0];
                                                                e3_1 = right_1.fields[2];
                                                            }
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else if (right_1.fields[0].tag === 3) {
                                                        if (right_1.fields[0].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = right_1.fields[0].fields[0];
                                                            a2 = left_1.fields[0];
                                                            b1 = right_1.fields[0].fields[2];
                                                            b2 = left_1.fields[2];
                                                            c1 = right_1.fields[2];
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else {
                                                        matchResult_8 = 1;
                                                        e1_13 = left_1;
                                                        e2_13 = right_1.fields[0];
                                                        e3_1 = right_1.fields[2];
                                                    }
                                                }
                                                else {
                                                    matchResult_8 = 1;
                                                    e1_13 = right_1;
                                                    e2_13 = left_1.fields[0];
                                                    e3_1 = left_1.fields[2];
                                                }
                                            }
                                            else {
                                                matchResult_8 = 1;
                                                e1_13 = right_1;
                                                e2_13 = left_1.fields[0];
                                                e3_1 = left_1.fields[2];
                                            }
                                        }
                                        else if (right_1.tag === 3) {
                                            if (right_1.fields[1].tag === 2) {
                                                if (right_1.fields[2].tag === 3) {
                                                    if (right_1.fields[2].fields[1].tag === 0) {
                                                        matchResult_8 = 0;
                                                        a1 = right_1.fields[2].fields[0];
                                                        a2 = left_1.fields[0];
                                                        b1 = right_1.fields[2].fields[2];
                                                        b2 = left_1.fields[2];
                                                        c1 = right_1.fields[0];
                                                    }
                                                    else if (left_1.fields[0].tag === 3) {
                                                        if (left_1.fields[0].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = left_1.fields[0].fields[0];
                                                            a2 = right_1.fields[0];
                                                            b1 = left_1.fields[0].fields[2];
                                                            b2 = right_1.fields[2];
                                                            c1 = left_1.fields[2];
                                                        }
                                                        else if (right_1.fields[0].tag === 3) {
                                                            if (right_1.fields[0].fields[1].tag === 0) {
                                                                matchResult_8 = 0;
                                                                a1 = right_1.fields[0].fields[0];
                                                                a2 = left_1.fields[0];
                                                                b1 = right_1.fields[0].fields[2];
                                                                b2 = left_1.fields[2];
                                                                c1 = right_1.fields[2];
                                                            }
                                                            else {
                                                                matchResult_8 = 1;
                                                                e1_13 = left_1;
                                                                e2_13 = right_1.fields[0];
                                                                e3_1 = right_1.fields[2];
                                                            }
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else if (right_1.fields[0].tag === 3) {
                                                        if (right_1.fields[0].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = right_1.fields[0].fields[0];
                                                            a2 = left_1.fields[0];
                                                            b1 = right_1.fields[0].fields[2];
                                                            b2 = left_1.fields[2];
                                                            c1 = right_1.fields[2];
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else {
                                                        matchResult_8 = 1;
                                                        e1_13 = left_1;
                                                        e2_13 = right_1.fields[0];
                                                        e3_1 = right_1.fields[2];
                                                    }
                                                }
                                                else if (left_1.fields[0].tag === 3) {
                                                    if (left_1.fields[0].fields[1].tag === 0) {
                                                        matchResult_8 = 0;
                                                        a1 = left_1.fields[0].fields[0];
                                                        a2 = right_1.fields[0];
                                                        b1 = left_1.fields[0].fields[2];
                                                        b2 = right_1.fields[2];
                                                        c1 = left_1.fields[2];
                                                    }
                                                    else if (right_1.fields[0].tag === 3) {
                                                        if (right_1.fields[0].fields[1].tag === 0) {
                                                            matchResult_8 = 0;
                                                            a1 = right_1.fields[0].fields[0];
                                                            a2 = left_1.fields[0];
                                                            b1 = right_1.fields[0].fields[2];
                                                            b2 = left_1.fields[2];
                                                            c1 = right_1.fields[2];
                                                        }
                                                        else {
                                                            matchResult_8 = 1;
                                                            e1_13 = left_1;
                                                            e2_13 = right_1.fields[0];
                                                            e3_1 = right_1.fields[2];
                                                        }
                                                    }
                                                    else {
                                                        matchResult_8 = 1;
                                                        e1_13 = left_1;
                                                        e2_13 = right_1.fields[0];
                                                        e3_1 = right_1.fields[2];
                                                    }
                                                }
                                                else if (right_1.fields[0].tag === 3) {
                                                    if (right_1.fields[0].fields[1].tag === 0) {
                                                        matchResult_8 = 0;
                                                        a1 = right_1.fields[0].fields[0];
                                                        a2 = left_1.fields[0];
                                                        b1 = right_1.fields[0].fields[2];
                                                        b2 = left_1.fields[2];
                                                        c1 = right_1.fields[2];
                                                    }
                                                    else {
                                                        matchResult_8 = 1;
                                                        e1_13 = left_1;
                                                        e2_13 = right_1.fields[0];
                                                        e3_1 = right_1.fields[2];
                                                    }
                                                }
                                                else {
                                                    matchResult_8 = 1;
                                                    e1_13 = left_1;
                                                    e2_13 = right_1.fields[0];
                                                    e3_1 = right_1.fields[2];
                                                }
                                            }
                                            else {
                                                matchResult_8 = 1;
                                                e1_13 = right_1;
                                                e2_13 = left_1.fields[0];
                                                e3_1 = left_1.fields[2];
                                            }
                                        }
                                        else {
                                            matchResult_8 = 1;
                                            e1_13 = right_1;
                                            e2_13 = left_1.fields[0];
                                            e3_1 = left_1.fields[2];
                                        }
                                    }
                                    else if (right_1.tag === 3) {
                                        if (right_1.fields[1].tag === 2) {
                                            matchResult_8 = 1;
                                            e1_13 = left_1;
                                            e2_13 = right_1.fields[0];
                                            e3_1 = right_1.fields[2];
                                        }
                                        else {
                                            matchResult_8 = 2;
                                            l_1 = left_1;
                                            r_1 = right_1;
                                        }
                                    }
                                    else {
                                        matchResult_8 = 2;
                                        l_1 = left_1;
                                        r_1 = right_1;
                                    }
                                }
                                else if (right_1.tag === 3) {
                                    if (right_1.fields[1].tag === 2) {
                                        matchResult_8 = 1;
                                        e1_13 = left_1;
                                        e2_13 = right_1.fields[0];
                                        e3_1 = right_1.fields[2];
                                    }
                                    else {
                                        matchResult_8 = 2;
                                        l_1 = left_1;
                                        r_1 = right_1;
                                    }
                                }
                                else {
                                    matchResult_8 = 2;
                                    l_1 = left_1;
                                    r_1 = right_1;
                                }
                                switch (matchResult_8) {
                                    case 0: {
                                        const matchValue_4 = evalExp(a1);
                                        const matchValue_5 = evalExp(a2);
                                        const matchValue_6 = evalExp(b1);
                                        const matchValue_7 = evalExp(b2);
                                        const matchValue_8 = evalExp(c1);
                                        const b2Eval = matchValue_7;
                                        const b1Eval = matchValue_6;
                                        const a2Eval = matchValue_5;
                                        const a1Eval = matchValue_4;
                                        if ((equals(a1Eval, a2Eval) && equals(b1Eval, b2Eval)) ? true : (equals(a1Eval, b2Eval) && equals(a2Eval, b1Eval))) {
                                            return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.CarryOfOp, new FastAlgExp(/* BinaryExp */ 3, [matchValue_8, BinaryOp.AddOp, new FastAlgExp(/* BinaryExp */ 3, [a1Eval, BinaryOp.AddOp, b1Eval])])]);
                                        }
                                        else {
                                            return new FastAlgExp(/* BinaryExp */ 3, [left_1, BinaryOp.BitOrOp, right_1]);
                                        }
                                    }
                                    case 1:
                                        if (equals(e1_13, e2_13) ? true : equals(e1_13, e3_1)) {
                                            return e1_13;
                                        }
                                        else if (equals(e1_13, new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NotOp, e2_13]))) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e1_13, BinaryOp.BitOrOp, e3_1]);
                                        }
                                        else if (equals(e1_13, new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NotOp, e3_1]))) {
                                            return new FastAlgExp(/* BinaryExp */ 3, [e1_13, BinaryOp.BitOrOp, e2_13]);
                                        }
                                        else {
                                            return new FastAlgExp(/* BinaryExp */ 3, [left_1, BinaryOp.BitOrOp, right_1]);
                                        }
                                    default:
                                        if (equals(l_1, r_1)) {
                                            return l_1;
                                        }
                                        else {
                                            return new FastAlgExp(/* BinaryExp */ 3, [l_1, BinaryOp.BitOrOp, r_1]);
                                        }
                                }
                            }
                        }
                    }
                }
            }
            case 7: {
                const left_2 = evalExp(exp1_2);
                const right_2 = evalExp(exp2_2);
                let matchResult_9, exp_20, exp_21, z_20;
                if (right_2.tag === 1) {
                    if (right_2.fields[0].Dat.tag === 1) {
                        if (left_2.tag === 1) {
                            if (left_2.fields[0].Dat.tag === 1) {
                                if (equals_1(right_2.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_9 = 1;
                                    exp_21 = left_2;
                                    z_20 = right_2.fields[0].Dat.fields[0];
                                }
                                else if (equals_1(left_2.fields[0].Dat.fields[0], fromZero())) {
                                    matchResult_9 = 1;
                                    exp_21 = right_2;
                                    z_20 = left_2.fields[0].Dat.fields[0];
                                }
                                else {
                                    matchResult_9 = 2;
                                }
                            }
                            else if (left_2.fields[0].Dat.fields[0] === 0) {
                                matchResult_9 = 0;
                                exp_20 = right_2;
                            }
                            else if (equals_1(right_2.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_9 = 1;
                                exp_21 = left_2;
                                z_20 = right_2.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_9 = 2;
                            }
                        }
                        else if (equals_1(right_2.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_9 = 1;
                            exp_21 = left_2;
                            z_20 = right_2.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_9 = 2;
                        }
                    }
                    else if (right_2.fields[0].Dat.fields[0] === 0) {
                        matchResult_9 = 0;
                        exp_20 = left_2;
                    }
                    else if (left_2.tag === 1) {
                        if (left_2.fields[0].Dat.tag === 1) {
                            if (equals_1(left_2.fields[0].Dat.fields[0], fromZero())) {
                                matchResult_9 = 1;
                                exp_21 = right_2;
                                z_20 = left_2.fields[0].Dat.fields[0];
                            }
                            else {
                                matchResult_9 = 2;
                            }
                        }
                        else if (left_2.fields[0].Dat.fields[0] === 0) {
                            matchResult_9 = 0;
                            exp_20 = right_2;
                        }
                        else {
                            matchResult_9 = 2;
                        }
                    }
                    else {
                        matchResult_9 = 2;
                    }
                }
                else if (left_2.tag === 1) {
                    if (left_2.fields[0].Dat.tag === 1) {
                        if (equals_1(left_2.fields[0].Dat.fields[0], fromZero())) {
                            matchResult_9 = 1;
                            exp_21 = right_2;
                            z_20 = left_2.fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult_9 = 2;
                        }
                    }
                    else if (left_2.fields[0].Dat.fields[0] === 0) {
                        matchResult_9 = 0;
                        exp_20 = right_2;
                    }
                    else {
                        matchResult_9 = 2;
                    }
                }
                else {
                    matchResult_9 = 2;
                }
                switch (matchResult_9) {
                    case 0:
                        return exp_20;
                    case 1:
                        return exp_21;
                    default: {
                        let matchResult_10, exp_22, n_4, w_12, exp_23, n_5, w_13, l_2, r_2;
                        if (right_2.tag === 1) {
                            if (right_2.fields[0].Dat.tag === 1) {
                                if (left_2.tag === 1) {
                                    if (left_2.fields[0].Dat.tag === 1) {
                                        matchResult_10 = 1;
                                        exp_23 = left_2;
                                        n_5 = right_2.fields[0].Dat.fields[0];
                                        w_13 = right_2.fields[0].Width;
                                    }
                                    else {
                                        matchResult_10 = 0;
                                        exp_22 = right_2;
                                        n_4 = left_2.fields[0].Dat.fields[0];
                                        w_12 = left_2.fields[0].Width;
                                    }
                                }
                                else {
                                    matchResult_10 = 1;
                                    exp_23 = left_2;
                                    n_5 = right_2.fields[0].Dat.fields[0];
                                    w_13 = right_2.fields[0].Width;
                                }
                            }
                            else {
                                matchResult_10 = 0;
                                exp_22 = left_2;
                                n_4 = right_2.fields[0].Dat.fields[0];
                                w_12 = right_2.fields[0].Width;
                            }
                        }
                        else if (left_2.tag === 1) {
                            if (left_2.fields[0].Dat.tag === 1) {
                                matchResult_10 = 1;
                                exp_23 = right_2;
                                n_5 = left_2.fields[0].Dat.fields[0];
                                w_13 = left_2.fields[0].Width;
                            }
                            else {
                                matchResult_10 = 0;
                                exp_22 = right_2;
                                n_4 = left_2.fields[0].Dat.fields[0];
                                w_12 = left_2.fields[0].Width;
                            }
                        }
                        else {
                            matchResult_10 = 2;
                            l_2 = left_2;
                            r_2 = right_2;
                        }
                        switch (matchResult_10) {
                            case 0:
                                if (n_4 === (toUInt32(bigIntMask(w_12)) >>> 0)) {
                                    return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NotOp, exp_22]);
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left_2, BinaryOp.BitXorOp, right_2]);
                                }
                            case 1:
                                if (equals_1(n_5, bigIntMask(w_13))) {
                                    return new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NotOp, exp_23]);
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [left_2, BinaryOp.BitXorOp, right_2]);
                                }
                            default:
                                if (equals(l_2, r_2)) {
                                    return zeroLiteral(getAlgExpWidth(l_2));
                                }
                                else if ((getAlgExpWidth(l_2) === 1) && (getAlgExpWidth(r_2) === 1)) {
                                    return reduceArithmetic(new FastAlgExp(/* BinaryExp */ 3, [l_2, BinaryOp.AddOp, r_2]));
                                }
                                else {
                                    return new FastAlgExp(/* BinaryExp */ 3, [l_2, BinaryOp.BitXorOp, r_2]);
                                }
                        }
                    }
                }
            }
            case 8:
                return reduceArithmetic(exp);
            case 9:
                return new FastAlgExp(/* ComparisonExp */ 4, [evalExp(exp_24), ComparisonOp.Equals, x]);
            default: {
                const evaluated_4 = map(evalExp, exps);
                const _arg = tryBitwiseOperation(evaluated_4);
                if (_arg == null) {
                    return new FastAlgExp(/* AppendExp */ 5, [foldAppends(evaluated_4)]);
                }
                else {
                    return _arg;
                }
            }
        }
        break;
    }
}

export function reduceArithmetic(expression) {
    let l;
    const updateExpCount = (exp, trackMap, action) => {
        const matchValue = tryFind(exp, trackMap);
        if (matchValue == null) {
            return add(exp, action(0), trackMap);
        }
        else {
            return add(exp, action(matchValue), trackMap);
        }
    };
    const width = getAlgExpWidth(expression) | 0;
    const flatLst = map(evalExp, flattenNestedArithmetic(expression));
    const patternInput = fold_1((tupledArg, expr) => {
        const numTrack = tupledArg[0];
        const expTrack = tupledArg[1];
        let matchResult, w, w_1, e;
        switch (expr.tag) {
            case 1: {
                if (expr.fields[0].Dat.tag === 0) {
                    matchResult = 0;
                    w = expr.fields[0].Dat.fields[0];
                }
                else {
                    matchResult = 3;
                }
                break;
            }
            case 2: {
                if (expr.fields[0].tag === 0) {
                    if (expr.fields[1].tag === 1) {
                        if (expr.fields[1].fields[0].Dat.tag === 0) {
                            matchResult = 1;
                            w_1 = expr.fields[1].fields[0].Dat.fields[0];
                        }
                        else {
                            matchResult = 2;
                            e = expr.fields[1];
                        }
                    }
                    else {
                        matchResult = 2;
                        e = expr.fields[1];
                    }
                }
                else {
                    matchResult = 3;
                }
                break;
            }
            default:
                matchResult = 3;
        }
        switch (matchResult) {
            case 0:
                return [op_Addition(numTrack, fromUInt32(w)), expTrack];
            case 1:
                return [op_Subtraction(numTrack, fromUInt32(w_1)), expTrack];
            case 2:
                return [numTrack, updateExpCount(e, expTrack, (x_1) => ((x_1 - 1) | 0))];
            default:
                return [numTrack, updateExpCount(expr, expTrack, (x) => ((x + 1) | 0))];
        }
    }, [fromZero(), empty({
        Compare: (x_2, y) => (compare(x_2, y) | 0),
    })], flatLst);
    const numVal = patternInput[0];
    const expCounts = patternInput[1];
    const expCounts_1 = (width === 1) ? map_1((_arg, count_1) => ((((count_1 % 2) + 2) % 2) | 0), expCounts) : expCounts;
    let constVal;
    const m = op_LeftShift(fromOne(), width);
    constVal = ((width === 1) ? op_Modulus(op_Addition(op_Modulus(numVal, m), m), m) : op_Modulus(numVal, m));
    const numDataExp = (compare_1(constVal, fromZero()) >= 0) ? valueLiteral(width, constVal) : (new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NegOp, valueLiteral(width, op_UnaryNegation(constVal))]));
    return assembleArithmetic(width, (l = collect((tupledArg_1) => {
        const exp_2 = tupledArg_1[0];
        const count_2 = tupledArg_1[1] | 0;
        if (count_2 === 0) {
            return empty_1();
        }
        else if (count_2 > 0) {
            return toList_1(delay(() => map_3((i) => exp_2, rangeDouble(1, 1, count_2))));
        }
        else {
            return toList_1(delay(() => map_3((i_1) => (new FastAlgExp(/* UnaryExp */ 2, [UnaryOp.NegOp, exp_2])), rangeDouble(1, 1, Math.abs(count_2)))));
        }
    }, toList(expCounts_1)), equals_1(constVal, fromZero()) ? l : append(l, singleton(numDataExp))));
}

export function fastBit(n) {
    return new FastData(new FastBits(/* Word */ 0, [n]), 1);
}

export function bitsToInt(lst) {
    if (!isEmpty_1(lst)) {
        return (equals(head(lst), Bit.Zero) ? 0 : 1) + (bitsToInt(tail(lst)) * 2);
    }
    else {
        return 0;
    }
}

export function bitsToBig(lst) {
    if (!isEmpty_1(lst)) {
        return op_Addition(equals(head(lst), Bit.Zero) ? fromZero() : fromOne(), op_LeftShift(bitsToBig(tail(lst)), 1));
    }
    else {
        return fromZero();
    }
}

/**
 * convert Wiredata to FastData equivalent
 */
export function wireToFast(wd) {
    const n = length(wd) | 0;
    return new FastData((n <= 32) ? (new FastBits(/* Word */ 0, [bitsToInt(wd)])) : (new FastBits(/* BigWord */ 1, [bitsToBig(wd)])), n);
}

/**
 * convert FastData to WireData equivalent
 */
export function fastToWire(f) {
    const matchValue = f.Dat;
    if (matchValue.tag === 1) {
        return map((n_1) => {
            if (equals_1(op_BitwiseAnd(matchValue.fields[0], bigIntBitMask(n_1)), fromZero())) {
                return Bit.Zero;
            }
            else {
                return Bit.One;
            }
        }, toList_1(rangeDouble(0, 1, f.Width - 1)));
    }
    else {
        return map((n) => {
            if (((matchValue.fields[0] & ((1 << n) >>> 0)) >>> 0) === 0) {
                return Bit.Zero;
            }
            else {
                return Bit.One;
            }
        }, toList_1(rangeDouble(0, 1, f.Width - 1)));
    }
}

export const fastDataZero = new FastData(new FastBits(/* Word */ 0, [0]), 1);

export const fastDataOne = new FastData(new FastBits(/* Word */ 0, [1]), 1);

export function b2s(b) {
    const lsw = op_BitwiseAnd(b, op_Subtraction(op_LeftShift(fromOne(), 32), fromOne()));
    const hex = `${interpolate("%08x%P()", [toUInt32(lsw) >>> 0])}`;
    const msws = op_RightShift(b, 32);
    if (!equals_1(msws, fromZero())) {
        return b2s(msws) + hex;
    }
    else {
        return hex;
    }
}

/**
 * Extract bit field (msb:lsb) from f. Bits are numbered little-endian from 0.
 * Note that for a single bit result the un-normalised version is used, so it will
 * be compatible with fast implementation of boolean logic.
 */
export function getBits(msb, lsb, f) {
    const outW = ((msb - lsb) + 1) | 0;
    const outWMask32 = (outW === 32) ? 4294967295 : (((1 << outW) >>> 0) - 1);
    const matchValue = f.Dat;
    if (matchValue.tag === 1) {
        const mask = bigIntMask(outW);
        const bits_1 = op_BitwiseAnd(op_RightShift(matchValue.fields[0], lsb), mask);
        return new FastData((outW <= 32) ? ((((compare_1(bits_1, fromZero()) < 0) ? true : (compare_1(bits_1, op_LeftShift(fromOne(), 32)) >= 0)) ? warn(`bits out of range for the width: bits = ${toString(bits_1, "X")} mask = ${mask} msb,lsb = (${msb},${lsb})`) : undefined, new FastBits(/* Word */ 0, [((toUInt32(bits_1) >>> 0) & outWMask32) >>> 0]))) : (new FastBits(/* BigWord */ 1, [op_BitwiseAnd(bits_1, bigIntMask(outW))])), outW);
    }
    else {
        return new FastData(new FastBits(/* Word */ 0, [((matchValue.fields[0] >>> lsb) & outWMask32) >>> 0]), outW);
    }
}

export function getBitsFromUInt32(msb, lsb, x) {
    const outW = ((msb - lsb) + 1) | 0;
    return ((x >>> lsb) & ((outW === 32) ? 4294967295 : (((1 << outW) >>> 0) - 1))) >>> 0;
}

export function getBitsFromBigInt(msb, lsb, x) {
    const outW = ((msb - lsb) + 1) | 0;
    const mask = bigIntMask(outW);
    return op_BitwiseAnd(op_BitwiseAnd(op_RightShift(x, lsb), mask), bigIntMask(outW));
}

export function getBitsFromBigIntToUInt32(msb, lsb, x) {
    const outW = ((msb - lsb) + 1) | 0;
    const outWMask32 = (outW === 32) ? 4294967295 : (((1 << outW) >>> 0) - 1);
    const mask = bigIntMask(outW);
    const bits = op_BitwiseAnd(op_RightShift(x, lsb), mask);
    if ((compare_1(bits, fromZero()) < 0) ? true : (compare_1(bits, op_LeftShift(fromOne(), 32)) >= 0)) {
        warn(`bits out of range for the width: bits = ${toString(bits, "X")} mask = ${mask} msb,lsb = (${msb},${lsb})`);
    }
    return ((toUInt32(bits) >>> 0) & outWMask32) >>> 0;
}

